import React, { FC, ReactElement, useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchDistributorInfo } from "../../redux-toolkit/user/user-thunks";
import DropKartLogo from "../../img/finwhite.png";
import DropKartDark from "../../img/fin.png";
import { BASE_URL } from "../../constants/urlConstants";
import { resetCartState } from "../../redux-toolkit/cart/cart-slice";
import { resetOrderState } from "../../redux-toolkit/order/order-slice";
import {
  GlobalOutlined,
  HomeOutlined,
  ShoppingCartOutlined,
  LoginOutlined,
  LogoutOutlined,
  SearchOutlined,
  UserOutlined,
  MenuOutlined,
  AppstoreOutlined,
  ProfileOutlined,
  OrderedListOutlined,
  EnvironmentOutlined,
  HeartOutlined,
  ShoppingOutlined,
  AppstoreAddOutlined,
  UnorderedListOutlined,
  TeamOutlined,
  LockOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Link, useHistory, useLocation } from "react-router-dom";
import { Affix, Badge, Input, message } from "antd";
import axios from "axios";

import { selectUserFromUserState } from "../../redux-toolkit/user/user-selector";
import { selectCartItemsCount } from "../../redux-toolkit/cart/cart-selector";
import {
  MENU,
  ACCOUNT_USER_INFO,
  ACCOUNT_USER_EDIT,
  ACCOUNT_USER_ORDERS,
  ACCOUNT_ADMIN_ADD,
  ACCOUNT_ADMIN_PERFUMES,
  ACCOUNT_ADMIN_ORDERS,
  ACCOUNT_ADMIN_DIST_ORDERS,
  ACCOUNT_ADMIN_USERS,
  ACCOUNT_ADMIN_HOME,
  ACCOUNT_ADMIN_PINCODES,
  ACCOUNT_ADMIN_ADD_DIST,
  ACCOUNT_ADMIN_ADD_ME,
  ACCOUNT_ADMIN_ADD_EMP,
  ACCOUNT_ADMIN_ADD_ADDRESS,
  ACCOUNT_ADMIN_DIST_STOCK,
  ACCOUNT_USER_PRIVACY,
} from "../../constants/routeConstants";
import "./NavBar.scss";

const CATEGORIES = [
  { icon: "🛍️", label: "All",         id: "all"      },
  { icon: "💧", label: "Mineral",      id: "mineral"  },
  { icon: "🫧", label: "Sparkling",    id: "sparkling"},
  { icon: "🥤", label: "Soft Drinks",  id: "soft"     },
  { icon: "🛢️", label: "Big Cans",    id: "can"      },
  { icon: "🍶", label: "Glass Bottle", id: "glass"    },
];

// ── Helper to read role flags from localStorage ──────────────
const readRoleFlags = () => {
  const role = (localStorage.getItem("role") ?? "").trim().toUpperCase();
  return {
    isME:          role === "2" || role === "ME"           || role === "ROLE_ME",
    isAdmin:       role === "1" || role === "ADMIN"        || role === "ROLE_ADMIN",
    isDistributor: role === "3" || role === "DISTRIBUTOR"  || role === "ROLE_DISTRIBUTOR",
    isEmployee:    role === "4" || role === "EMPLOYEE"     || role === "ROLE_EMPLOYEE",
  };
};

const NavBar: FC = (): ReactElement => {
  const dispatch       = useDispatch();
  const cartItemsCount = useSelector(selectCartItemsCount);
  useSelector(selectUserFromUserState);

  const history  = useHistory();
  const location = useLocation();

  const [text, setText]             = useState<string>("");
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [loading, setLoading]       = useState<boolean>(false);
  const [isMobile, setIsMobile]     = useState<boolean>(window.innerWidth <= 900);
  const [activeCategory, setActiveCategory] = useState<string>("all");

  // ── Mobile search overlay state ──
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [mobileText, setMobileText] = useState<string>("");
  const mobileSearchRef = useRef<HTMLInputElement>(null);

  // ✅ Reactive auth state — reads from localStorage, updates on storage events
  const [token, setToken] = useState<string | null>(localStorage.getItem("token"));
  const [name,  setName]  = useState<string | null>(localStorage.getItem("name"));
  const [isME,          setIsME]          = useState(false);
  const [isAdmin,       setIsAdmin]       = useState(false);
  const [isDistributor, setIsDistributor] = useState(false);
  const [isEmployee,    setIsEmployee]    = useState(false);

  // ✅ Sync all auth state from localStorage
  const syncAuthState = useCallback(() => {
    setToken(localStorage.getItem("token"));
    setName(localStorage.getItem("name"));
    const flags = readRoleFlags();
    setIsME(flags.isME);
    setIsAdmin(flags.isAdmin);
    setIsDistributor(flags.isDistributor);
    setIsEmployee(flags.isEmployee);
  }, []);

  // ✅ Run on mount + listen for storage changes (login/logout in same tab too)
  useEffect(() => {
    syncAuthState();
    window.addEventListener("storage", syncAuthState);
    return () => window.removeEventListener("storage", syncAuthState);
  }, [syncAuthState]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (drawerOpen) document.body.style.overflow = "hidden";
    else            document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  useEffect(() => {
    if (searchOpen) {
      setTimeout(() => mobileSearchRef.current?.focus(), 100);
    } else {
      setMobileText("");
    }
  }, [searchOpen]);

  // ── Desktop search ──
  const handleSearch = useCallback(async (): Promise<void> => {
    const query = text.trim();
    if (!query) return;
    setLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/api/v1/perfumes/search/global/${query}`);
      const perfumes = res.data;
      if (!perfumes?.length) { message.info("No products found."); return; }
      history.push({ pathname: MENU, state: { id: "all", searchText: query, perfumes } });
    } catch {
      message.error("Failed to fetch search results.");
    } finally {
      setLoading(false);
    }
  }, [text, history]);

  // ── Mobile search ──
  const handleMobileSearch = useCallback(async (): Promise<void> => {
    const query = mobileText.trim();
    if (!query) {
      setSearchOpen(false);
      history.push({ pathname: MENU, state: { id: "all" } });
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/api/v1/perfumes/search/global/${query}`);
      const perfumes = res.data;
      setSearchOpen(false);
      if (!perfumes?.length) { message.info("No products found."); return; }
      history.push({ pathname: MENU, state: { id: "all", searchText: query, perfumes } });
    } catch {
      message.error("Failed to fetch search results.");
    } finally {
      setLoading(false);
    }
  }, [mobileText, history]);

  // ── Logout ──
  const handleLogout = () => {
    dispatch(resetCartState());
    dispatch(resetOrderState());
    localStorage.clear();
    // ✅ Notify same-tab listeners (storage event only fires cross-tab by default)
    window.dispatchEvent(new Event("storage"));
    syncAuthState();
    setDrawerOpen(false);
    window.location.href = "/login";
  };

  const go = (path: string): void => {
    setDrawerOpen(false);
    history.push(path);
  };

  const goHome = (): void => {
    setDrawerOpen(false);
    history.push(isME ? "/fin/dashboard" : "/");
  };

  const handleCategory = (id: string): void => {
    setActiveCategory(id);
    setDrawerOpen(false);
    if (id === "all") history.push("/menu");
    else history.push(`/menu?search=${encodeURIComponent(id)}`);
  };

  const activeTab =
    location.pathname.startsWith("/cart")    ? "cart"     :
    location.pathname.startsWith("/account") ? "account"  :
    location.pathname.startsWith("/menu")    ? "products" : "home";

  /* ── Role-based drawer menu items ── */
  const DrawerAccountItems = () => {
    if (isAdmin) return (
      <>
        <div className="mob-drawer-section">Admin</div>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_USER_INFO)}>
          <UserOutlined className="mob-drawer-item-icon" /><span>Personal Data</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_ADD)}>
          <AppstoreAddOutlined className="mob-drawer-item-icon" /><span>Add Product</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_PERFUMES)}>
          <UnorderedListOutlined className="mob-drawer-item-icon" /><span>List of Products</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_ORDERS)}>
          <ShoppingOutlined className="mob-drawer-item-icon" /><span>All Orders</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_USERS)}>
          <TeamOutlined className="mob-drawer-item-icon" /><span>All Users</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_HOME)}>
          <HomeOutlined className="mob-drawer-item-icon" /><span>Manage Home Page</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_PINCODES)}>
          <EnvironmentOutlined className="mob-drawer-item-icon" /><span>Manage Pincodes</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_ADD_DIST)}>
          <EnvironmentOutlined className="mob-drawer-item-icon" /><span>Manage Distributors</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_ADD_ME)}>
          <EnvironmentOutlined className="mob-drawer-item-icon" /><span>Manage MEs</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_ADD_EMP)}>
          <EnvironmentOutlined className="mob-drawer-item-icon" /><span>Manage Employees</span>
        </button>
      </>
    );

    if (isDistributor) return (
      <>
        <div className="mob-drawer-section">Distributor</div>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_USER_INFO)}>
          <UserOutlined className="mob-drawer-item-icon" /><span>Profile Data</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_DIST_ORDERS)}>
          <ShoppingOutlined className="mob-drawer-item-icon" /><span>My User Orders</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_USERS)}>
          <TeamOutlined className="mob-drawer-item-icon" /><span>My Users</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_ADD_ADDRESS)}>
          <EnvironmentOutlined className="mob-drawer-item-icon" /><span>My Address</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_DIST_STOCK)}>
          <EnvironmentOutlined className="mob-drawer-item-icon" /><span>My Stock</span>
        </button>
      </>
    );

    if (isEmployee) return (
      <>
        <div className="mob-drawer-section">Employee</div>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_USER_INFO)}>
          <UserOutlined className="mob-drawer-item-icon" /><span>Personal Data</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_PERFUMES)}>
          <UnorderedListOutlined className="mob-drawer-item-icon" /><span>List of Products</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_ORDERS)}>
          <ShoppingOutlined className="mob-drawer-item-icon" /><span>All Orders</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_USERS)}>
          <TeamOutlined className="mob-drawer-item-icon" /><span>All Users</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_HOME)}>
          <HomeOutlined className="mob-drawer-item-icon" /><span>Manage Home Page</span>
        </button>
      </>
    );

    if (isME) return (
      <>
        <div className="mob-drawer-section">Marketing Executive</div>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_USER_INFO)}>
          <UserOutlined className="mob-drawer-item-icon" /><span>Profile Data</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_DIST_ORDERS)}>
          <ShoppingOutlined className="mob-drawer-item-icon" /><span>All User Orders</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_USERS)}>
          <TeamOutlined className="mob-drawer-item-icon" /><span>All Users</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_ADD_DIST)}>
          <EnvironmentOutlined className="mob-drawer-item-icon" /><span>Manage Distributors</span>
        </button>
      </>
    );

    return (
      <>
        <div className="mob-drawer-section">Account</div>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_USER_INFO)}>
          <ProfileOutlined className="mob-drawer-item-icon" /><span>My Profile</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_USER_ORDERS)}>
          <OrderedListOutlined className="mob-drawer-item-icon" /><span>My Orders</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_ADD_ADDRESS)}>
          <EnvironmentOutlined className="mob-drawer-item-icon" /><span>My Address</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_ADMIN_DIST_STOCK)}>
          <EnvironmentOutlined className="mob-drawer-item-icon" /><span>Distributor Stock</span>
        </button>
        <button className="mob-drawer-item" onClick={() => go(ACCOUNT_USER_PRIVACY)}>
          <LockOutlined className="mob-drawer-item-icon" /><span>Privacy & Policy</span>
        </button>
      </>
    );
  };

  // ─── DESKTOP NAVBAR ──────────────────────────────────────────
  const DesktopNav = (
    <Affix offsetTop={0}>
      <div className="fk-navbar">
        <div className="fk-topbar">
          <div className="fk-logo" onClick={goHome}>
            <img src={DropKartLogo} alt="Finllie" className="fk-logo-img" />
          </div>

          <div className="fk-search-wrap">
            <Input
              prefix={<SearchOutlined className="fk-search-icon" />}
              placeholder="Search for products, brands and more"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
              disabled={loading}
              className="fk-search-input"
            />
            <button className="fk-search-btn" onClick={handleSearch} disabled={loading}>
              <SearchOutlined />
              {!loading ? " Search" : " ..."}
            </button>
          </div>

          <nav className="fk-nav-links">
            <button className="fk-nav-item" onClick={goHome}>
              <HomeOutlined /><span>Home</span>
            </button>
            <button className="fk-nav-item" onClick={() => history.push("/about")}>
              <GlobalOutlined /><span>About</span>
            </button>
            {!isME && (
              <>
                <button className="fk-nav-item" onClick={() => history.push({ pathname: MENU, state: { id: "all" } })}>
                  <AppstoreOutlined /><span>Products</span>
                </button>
                <button className="fk-nav-item fk-nav-cart" onClick={() => history.push("/cart")}>
                  <Badge
                    count={cartItemsCount}
                    size="small"
                    style={{ backgroundColor: "#f0c14b", color: "#2874f0", fontWeight: 700, boxShadow: "none" }}
                  >
                    <ShoppingCartOutlined style={{ color: "#ffffff", fontSize: 20 }} />
                  </Badge>
                  <span>Cart</span>
                </button>
              </>
            )}

            {token ? (
              <>
                <button className="fk-nav-item" onClick={() => history.push(ACCOUNT_USER_INFO)}>
                  <UserOutlined /><span>{name ?? "Profile"}</span>
                </button>
                <button className="fk-nav-item fk-nav-logout" onClick={handleLogout}>
                  <LogoutOutlined /><span>Logout</span>
                </button>
              </>
            ) : (
              <button className="fk-nav-item fk-nav-login" onClick={() => history.push("/login")}>
                <LoginOutlined /><span>Login</span>
              </button>
            )}
          </nav>
        </div>

        {!isME && (
          <div className="fk-subnav">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                className={`fk-cat-item ${activeCategory === cat.id ? "active" : ""}`}
                onClick={() => handleCategory(cat.id)}
              >
                <span className="fk-cat-icon">{cat.icon}</span>
                <span className="fk-cat-label">{cat.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </Affix>
  );

  // ─── MOBILE NAVBAR ────────────────────────────────────────────
  const MobileNav = (
    <>
      {/* ── Mobile search overlay ── */}
      {searchOpen && (
        <div className="mob-search-overlay">
          <div className="mob-search-overlay-bar">
            <button className="mob-search-overlay-back" onClick={() => setSearchOpen(false)}>
              <CloseOutlined />
            </button>
            <input
              ref={mobileSearchRef}
              className="mob-search-overlay-input"
              placeholder="Search for products..."
              value={mobileText}
              onChange={(e) => setMobileText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleMobileSearch()}
            />
            <button
              className="mob-search-overlay-btn"
              onClick={handleMobileSearch}
              disabled={loading}
            >
              {loading ? "..." : <SearchOutlined />}
            </button>
          </div>
        </div>
      )}

      <Affix offsetTop={0}>
        <div className="mob-topbar">
          <button className="mob-hamburger" onClick={() => setDrawerOpen(true)} aria-label="Open menu">
            <MenuOutlined />
          </button>

          <div className="mob-search" onClick={() => setSearchOpen(true)}>
            <SearchOutlined className="mob-search-icon" />
            <span className="mob-search-placeholder">Search for products...</span>
          </div>

          {!isME && (
            <button className="mob-cart-btn" onClick={() => history.push("/cart")}>
              <Badge
                count={cartItemsCount}
                size="small"
                style={{ backgroundColor: "#f0c14b", color: "#2874f0", fontWeight: 700, boxShadow: "none" }}
              >
                <ShoppingCartOutlined style={{ color: "#ffffff", fontSize: 22 }} />
              </Badge>
            </button>
          )}
        </div>
      </Affix>

      {!isME && (
        <div className="mob-cat-scroll">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              className={`mob-cat-chip ${activeCategory === cat.id ? "active" : ""}`}
              onClick={() => handleCategory(cat.id)}
            >
              <span className="mob-cat-icon">{cat.icon}</span>
              <span className="mob-cat-label">{cat.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Drawer overlay */}
      <div
        className={`mob-drawer-overlay ${drawerOpen ? "open" : ""}`}
        onClick={() => setDrawerOpen(false)}
      />

      {/* Drawer panel */}
      <div className={`mob-drawer ${drawerOpen ? "open" : ""}`}>
        <div className="mob-drawer-header">
          <img src={DropKartDark} alt="Finllie" className="mob-drawer-logo" />
          {token ? (
            <div className="mob-drawer-user">
              <div className="mob-drawer-avatar"><UserOutlined /></div>
              <div className="mob-drawer-user-info">
                <span className="mob-drawer-username">{name ?? "User"}</span>
                <span className="mob-drawer-usersub">View profile &amp; orders</span>
              </div>
            </div>
          ) : (
            <button className="mob-drawer-login-btn" onClick={() => go("/login")}>
              <LoginOutlined /> Sign In
            </button>
          )}
        </div>

        <div className="mob-drawer-body">
          <button className="mob-drawer-item" onClick={goHome}>
            <HomeOutlined className="mob-drawer-item-icon" /><span>Home</span>
          </button>

          {!isME && !isAdmin && !isDistributor && !isEmployee && (
            <>
              <div className="mob-drawer-section">Shop</div>
              <button className="mob-drawer-item" onClick={() => go(MENU)}>
                <AppstoreOutlined className="mob-drawer-item-icon" /><span>All Products</span>
              </button>
              {CATEGORIES.slice(1).map((cat) => (
                <button key={cat.id} className="mob-drawer-item"
                  onClick={() => { handleCategory(cat.id); }}
                >
                  <span className="mob-drawer-emoji">{cat.icon}</span>
                  <span>{cat.label}</span>
                </button>
              ))}
            </>
          )}

          {token && <DrawerAccountItems />}

          <div className="mob-drawer-divider" />

          {token ? (
            <button className="mob-drawer-item mob-drawer-logout" onClick={handleLogout}>
              <LogoutOutlined className="mob-drawer-item-icon" /><span>Logout</span>
            </button>
          ) : (
            <button className="mob-drawer-item" onClick={() => go("/login")}>
              <LoginOutlined className="mob-drawer-item-icon" /><span>Sign In</span>
            </button>
          )}

          <div className="mob-drawer-safe-bottom" />
        </div>
      </div>

      {/* Bottom navigation */}
      <nav className="mob-bottom-nav">
        <button className={`mob-bn-item ${activeTab === "home" ? "active" : ""}`} onClick={goHome}>
          <HomeOutlined className="mob-bn-icon" /><span>Home</span>
        </button>

        {!isME && !isAdmin && !isDistributor && !isEmployee && (
          <button
            className={`mob-bn-item ${activeTab === "products" ? "active" : ""}`}
            onClick={() => history.push({ pathname: MENU, state: { id: "all" } })}
          >
            <AppstoreOutlined className="mob-bn-icon" /><span>Products</span>
          </button>
        )}

        <button
          className={`mob-bn-item ${activeTab === "account" ? "active" : ""}`}
          onClick={() => history.push(token ? ACCOUNT_USER_INFO : "/login")}
        >
          <UserOutlined className="mob-bn-icon" /><span>Account</span>
        </button>

        {!isME && !isAdmin && !isDistributor && !isEmployee && (
          <button
            className={`mob-bn-item ${activeTab === "cart" ? "active" : ""}`}
            onClick={() => history.push("/cart")}
          >
            <Badge count={cartItemsCount} size="small" className="mob-bn-badge">
              <ShoppingCartOutlined className="mob-bn-icon" />
            </Badge>
            <span>Cart</span>
          </button>
        )}

        {(isAdmin || isDistributor) && (
          <button
            className={`mob-bn-item ${location.pathname.includes("orders") ? "active" : ""}`}
            onClick={() => history.push(isAdmin ? ACCOUNT_ADMIN_ORDERS : ACCOUNT_ADMIN_DIST_ORDERS)}
          >
            <ShoppingOutlined className="mob-bn-icon" /><span>Orders</span>
          </button>
        )}
      </nav>
    </>
  );

  return isMobile ? MobileNav : DesktopNav;
};

export default NavBar;